import React, { useRef, useState } from 'react'
import { motion, AnimatePresence } from 'motion/react'
import { Camera, Link2, ChevronDown, ChevronUp, Maximize2, Trash2 } from 'lucide-react'
import ImageCropModal from './ImageCropModal'
import ImageLightbox from './ImageLightbox'

interface PictureFieldEditorProps {
  fieldName: string
  value: string
  onUrlChange: (url: string) => void
  uploading: boolean
  onCroppedFile: (file: File) => void | Promise<void>
  /** If provided, a trash button is shown that clears the field; the
   * backend's items.update GC then deletes any orphaned S3 object on save. */
  onRemove?: () => void
}

const PictureFieldEditor: React.FC<PictureFieldEditorProps> = ({
  fieldName,
  value,
  onUrlChange,
  uploading,
  onCroppedFile,
  onRemove,
}) => {
  const fileInputRef = useRef<HTMLInputElement | null>(null)
  const [pendingCrop, setPendingCrop] = useState<File | null>(null)
  const [showLightbox, setShowLightbox] = useState(false)
  const [showUrl, setShowUrl] = useState(false)

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    // Reset so re-picking the same file fires onChange again (after a cancel).
    event.target.value = ''
    if (file) {
      setPendingCrop(file)
    }
  }

  return (
    <div className="space-y-3">
      <div>
        <div className="relative">
          <input
            ref={fileInputRef}
            type="file"
            className="hidden"
            accept="image/*"
            onChange={handleFileSelect}
          />
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-action flex items-center justify-center gap-2 transition-colors disabled:opacity-50"
            disabled={uploading}
          >
            <Camera className="w-4 h-4" />
            {uploading ? 'Uploading...' : 'Take Photo / Select File'}
          </button>
        </div>
      </div>

      {value && (
        <div className="mt-2 relative inline-block">
          <button
            type="button"
            onClick={() => setShowLightbox(true)}
            className="block group focus:outline-none"
            aria-label="View full image"
          >
            <img
              src={value}
              alt="Preview"
              className="w-full max-w-xs h-32 object-contain bg-gray-50 border border-gray-200 rounded-lg group-hover:opacity-90 transition-opacity cursor-zoom-in"
            />
          </button>
          <div className="absolute top-1.5 right-1.5 flex gap-1">
            <motion.button
              type="button"
              onClick={() => setShowLightbox(true)}
              className="p-1 bg-black/40 hover:bg-black/60 text-white rounded-md backdrop-blur-sm"
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              transition={{ type: 'spring', stiffness: 400, damping: 20 }}
              aria-label="View full image"
            >
              <Maximize2 className="w-3.5 h-3.5" />
            </motion.button>
            {onRemove && (
              <motion.button
                type="button"
                onClick={onRemove}
                className="p-1 bg-danger/70 hover:bg-danger-dark/90 text-white rounded-md backdrop-blur-sm"
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                transition={{ type: 'spring', stiffness: 400, damping: 20 }}
                aria-label="Remove image"
                title="Remove image"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </motion.button>
            )}
          </div>
        </div>
      )}

      <div>
        <motion.button
          type="button"
          onClick={() => setShowUrl((s) => !s)}
          className="text-xs text-gray-500 hover:text-gray-700 inline-flex items-center gap-1"
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          transition={{ type: 'spring', stiffness: 400, damping: 20 }}
        >
          <Link2 className="w-3 h-3" />
          {value ? 'Edit image URL' : 'Use image URL instead'}
          {showUrl ? (
            <ChevronUp className="w-3 h-3" />
          ) : (
            <ChevronDown className="w-3 h-3" />
          )}
        </motion.button>

        <AnimatePresence>
          {showUrl && (
            <motion.div
              initial={{ opacity: 0, y: -4, height: 0 }}
              animate={{ opacity: 1, y: 0, height: 'auto' }}
              exit={{ opacity: 0, y: -4, height: 0 }}
              transition={{ type: 'spring', stiffness: 500, damping: 30 }}
              className="overflow-hidden"
            >
              <input
                type="url"
                value={value}
                onChange={(e) => onUrlChange(e.target.value)}
                className="mt-2 w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-action"
                placeholder="https://example.com/image.jpg"
                aria-label={`${fieldName} image URL`}
              />
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {pendingCrop && (
        <ImageCropModal
          file={pendingCrop}
          onCancel={() => setPendingCrop(null)}
          onConfirm={async (cropped) => {
            setPendingCrop(null)
            await onCroppedFile(cropped)
          }}
        />
      )}

      {showLightbox && value && (
        <ImageLightbox
          src={value}
          alt={fieldName}
          onClose={() => setShowLightbox(false)}
        />
      )}
    </div>
  )
}

export default PictureFieldEditor
